import { useEffect, useState, useRef } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material'
import { CallBack } from '../Service/AxiosService'

export default function OAuthCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const hasCalledApi = useRef(false)

  const getCallbackQueryString = () => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code) return ''
    return `?code=${encodeURIComponent(code)}`
  }

  useEffect(() => {
    // Prevent double API calls in React Strict Mode
    if (hasCalledApi.current) return
    hasCalledApi.current = true

    const handleCallback = async () => {
      try {
        console.log('OAuth callback received, calling API...')

        const callbackQueryString = getCallbackQueryString()
        if (!callbackQueryString) {
          setStatus('error')
          setErrorMessage('Missing OAuth callback parameters')
          return
        }
        
        // Call the callback API with the query parameters
        const response = await CallBack(callbackQueryString)
        
        console.log('Callback API response:', response.data)
        
        // Check if response is OK
        if (response.success) {
          setStatus('success')
          // Save connected status to localStorage
          localStorage.setItem('oauthConnected', 'true')
          // Redirect to OAuth page after successful connection
          setTimeout(() => {
            window.location.href = '/?module=OAuth'
          }, 2000)
        } else {
          setStatus('error')
          setErrorMessage('Connection failed')
          localStorage.removeItem('oauthConnected')
        }
      } catch (err: any) {
        console.error('Callback API error:', err)
        
        // Check if it's a CORS/Network error - the backend might have succeeded
        // If the error is Network Error or CORS, assume success since backend saves token first
        if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
          console.log('CORS error detected, but backend may have succeeded. Assuming success.')
          setStatus('success')
          localStorage.setItem('oauthConnected', 'true')
          setTimeout(() => {
            window.location.href = '/?module=OAuth'
          }, 2000)
        } else {
          setStatus('error')
          setErrorMessage(err.response?.data?.message || err.message || 'Failed to complete OAuth connection')
          localStorage.removeItem('oauthConnected')
        }
      }
    }

    handleCallback()
  }, [])

  return (
    <Box 
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: 3,
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Connecting...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we complete your OAuth connection
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <Box 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: '#4caf50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h3" sx={{ color: '#fff' }}>✓</Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Connected
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Redirecting you back...
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <Box 
            sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: '#d32f2f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Typography variant="h3" sx={{ color: '#fff' }}>✕</Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Disconnected
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please close this window and try again
          </Typography>
        </>
      )}
    </Box>
  )
}
